import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';

import { useUpdateSubscriptionInvoiceLineItems } from '~entities/student/api/update-subscription-invoices';
import { type SubscriptionInvoice } from '~entities/student/modal/payment';

import { InvoiceDiscountManager } from '~shared/components/discount-manager';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import { DropdownMenuItem } from '~shared/shadcn/ui/dropdown-menu';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~shared/shadcn/ui/form';
import { Input } from '~shared/shadcn/ui/input';

interface EditUpcomingChargesProps {
  invoice: SubscriptionInvoice;
  studentId: string;
}

const formSchema = z.object({
  tuitionFee: z
    .number()
    .min(1, 'Tuition fee must be greater than 0')
    .max(10000, 'Tuition fee cannot exceed 10000'),
  appliedDiscount: z.number(),
});

type FormValues = z.infer<typeof formSchema>;

export const EditUpcomingCharges = ({
  invoice,
  studentId,
}: EditUpcomingChargesProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { mutateAsync: updateSubscriptionInvoiceLineItems, isPending } =
    useUpdateSubscriptionInvoiceLineItems();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tuitionFee: Number(invoice.amount) || 0,
      appliedDiscount: Number(invoice.appliedDiscount) || 0,
    },
  });

  const total = form.watch('tuitionFee') - form.watch('appliedDiscount');

  const handleDiscountChange = useCallback(
    (discountAmount: number) => {
      form.setValue('appliedDiscount', Number(discountAmount) || 0, {
        shouldDirty: true,
      });
    },
    [form],
  );

  const handleSaveChargeEdit = async (values: FormValues) => {
    const lineItems = [
      {
        productName: invoice.productName,
        amount: values.tuitionFee,
        type: invoice.type,
      },
    ];

    try {
      await updateSubscriptionInvoiceLineItems({
        studentId,
        invoiceId: invoice.invoiceId,
        lineItems,
      });
      toast.success('Amount updated successfully');
      setIsOpen(false);
    } catch (error) {
      console.error('Error updating charge:', error);
      toast.error('Failed to update charge amount');
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      form.reset({
        tuitionFee: Number(invoice.amount) || 0,
        appliedDiscount: Number(invoice.appliedDiscount) || 0,
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
          }}
        >
          Edit Upcoming Charge
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Upcoming Charges</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            className='space-y-4'
            onSubmit={form.handleSubmit(handleSaveChargeEdit)}
          >
            <FormField
              control={form.control}
              name='tuitionFee'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{invoice.productName}</FormLabel>
                  <FormControl>
                    <Input
                      type='number'
                      {...field}
                      onChange={(e) => {
                        field.onChange(Number(e.target.value));
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {invoice.productName === 'Tuition Fee' && (
              <FormField
                control={form.control}
                name='appliedDiscount'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Discount</FormLabel>
                    <FormControl>
                      <Input
                        type='number'
                        disabled
                        {...field}
                        onChange={(e) => {
                          field.onChange(Number(e.target.value));
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {invoice.productName === 'Tuition Fee' && (
              <FormItem>
                <FormLabel>Total</FormLabel>
                <FormControl>
                  <Input type='number' disabled value={total} />
                </FormControl>
              </FormItem>
            )}
          </form>
        </Form>

        {/* Discount Manager for Tuition Fee invoices */}
        {invoice.type === 'Tuition Fee' && (
          <InvoiceDiscountManager
            invoiceId={invoice.invoiceId}
            className='mt-4'
            onDiscountChange={handleDiscountChange}
          />
        )}

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => {
              setIsOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={form.handleSubmit(handleSaveChargeEdit)}
            disabled={isPending}
          >
            {isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
